import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Power, Eye, Users, UserCog, UserCheck, UserPlus, User, type LucideIcon } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { StatusDot } from '@/components/ui/StatusDot'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { type PageSize } from '@/hooks/useCrudList'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import type { PaginationMeta } from '@/types/api'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { cn } from '@/lib/cn'
import { PartnerService, type NetworkUser } from '@/services/modules/PartnerService'
import { UsersService } from '@/services/modules/UsersService'
import { useAuthStore, type AuthenticatedUser } from '@/stores/authStore'

const TYPE_TAB: Record<string, { label: string; icon: LucideIcon }> = {
  DIRETOR:    { label: 'Diretores',    icon: UserCog },
  SUPERVISOR: { label: 'Supervisores', icon: UserCheck },
  AFILIADO:   { label: 'Afiliados',    icon: UserPlus },
  VENDEDOR:   { label: 'Vendedores',   icon: User },
}
const TYPE_LABEL: Record<string, string> = { DIRETOR: 'Diretor', SUPERVISOR: 'Supervisor', AFILIADO: 'Afiliado', VENDEDOR: 'Vendedor' }

export function PartnerNetworkPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<NetworkUser[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<PageSize>(25)
  const [allowed, setAllowed] = useState<string[]>([])
  const [type, setType] = useState<string>('') // '' = Todos

  const sf = useStatusFilter({ searchPlaceholder: 'Buscar na rede' })

  // Abas (grupo de botões acima da tabela): Todos + os cargos diretos que este
  // parceiro pode ter (= os que ele cadastra). Ex.: diretor → Sup./Afiliado.
  const tabs = useMemo(() => ['', ...allowed], [allowed])
  const filters = useMemo(() => ({ ...sf.filters, ...(type ? { type } : {}) }), [sf.filters, type])

  const load = useCallback(async () => {
    setLoading(true)
    try { const res = await PartnerService.network({ page, size, ...filters }); setRows(res.data); setMeta(res.meta) }
    catch { toast.error('Erro ao carregar a rede.') }
    finally { setLoading(false) }
  }, [page, size, filters])
  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(1) }, [filters])
  useEffect(() => { PartnerService.allowedTypes().then(setAllowed).catch(() => {}) }, [])

  const canCreate = allowed.length > 0

  async function toggleStatus(u: NetworkUser) {
    const next = u.status === 'ATIVO' ? 'INATIVO' : 'ATIVO'
    try { await PartnerService.networkStatus(u.id, next); toast.success({ title: 'Status atualizado', description: u.name }); load() }
    catch (e) { toast.error({ title: 'Erro', description: extractApiError(e).message }) }
  }

  const startImpersonation = useAuthStore(s => s.startImpersonation)
  async function handleView(u: NetworkUser) {
    try {
      const data = await UsersService.impersonate(u.id)
      const { accessToken, ...user } = data
      startImpersonation(user as unknown as AuthenticatedUser, accessToken, u.name)
      toast.success({ title: 'Visualizando painel', description: u.name })
      navigate('/partner/dashboard')
    } catch (e) {
      toast.error({ title: 'Não foi possível abrir o painel', description: extractApiError(e).message })
    }
  }

  const columns: DataTableColumn<NetworkUser>[] = [
    { key: 'name', header: 'Nome', alwaysVisible: true, render: u => (
      <div><div className="font-medium text-gray-800">{u.name}</div><div className="text-[11px] text-gray-400">@{u.username}</div></div>
    ) },
    { key: 'email', header: 'E-mail', render: u => u.email ?? <span className="text-gray-400">—</span> },
    { key: 'type', header: 'Cargo', align: 'center', render: u => <Badge variant="blue">{TYPE_LABEL[u.type] ?? u.type}</Badge> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24', render: u => <StatusDot value={u.status as 'ATIVO' | 'INATIVO'} /> },
  ]

  return (
    <>
      <PageHeader title="Minha rede" subtitle="Os membros diretos da sua rede de vendas"
        crumbs={[{ label: 'Rede' }, { label: 'Minha rede' }]}
        actions={canCreate ? <Button onClick={() => navigate('/partner/network/novo')} iconLeft={<Plus size={14} />}>Adicionar à rede</Button> : undefined}
      />

      {/* Grupo de botões — filtro por cargo (acima da tabela, igual ao admin) */}
      {allowed.length > 0 && (
        <div className="mb-3 grid grid-flow-col auto-cols-fr rounded-md ring-1 ring-gray-200 bg-white overflow-hidden max-w-2xl">
          {tabs.map((t, i) => {
            const active = type === t
            const m = t === '' ? { label: 'Todos', icon: Users } : (TYPE_TAB[t] ?? { label: t, icon: User })
            return (
              <button key={t || 'all'} type="button" onClick={() => { setType(t); setPage(1) }} aria-pressed={active}
                className={cn(
                  'cursor-pointer w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-medium transition',
                  i > 0 && 'border-l border-gray-200',
                  active ? 'bg-brand-500 text-white hover:bg-brand-600' : 'text-gray-600 hover:bg-gray-50',
                )}>
                <m.icon size={14} /> {m.label}
              </button>
            )
          })}
        </div>
      )}

      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="partner.network" columns={columns} rows={rows} loading={loading} getRowKey={u => u.id}
        emptyMessage="Nenhum membro na sua rede."
        rowActions={u => [
          { key: 'view', label: 'Visualizar painel', icon: <Eye size={14} />, onClick: () => handleView(u) },
          { key: 'edit', label: 'Editar', icon: <Pencil size={14} />, divider: true, onClick: () => navigate(`/partner/network/${u.id}/editar`) },
          { key: 'status', label: u.status === 'ATIVO' ? 'Inativar' : 'Ativar', icon: <Power size={14} />, danger: u.status === 'ATIVO', divider: true, onClick: () => toggleStatus(u) },
        ]}
      />
      <Pagination meta={meta} size={size} onChange={setPage} onSizeChange={setSize} />
      {sf.drawer}
    </>
  )
}

export default PartnerNetworkPage
